import { useEffect, useMemo, useRef, useState } from "react";
import {
  Search, Bell, HelpCircle, ChevronLeft, ChevronRight, Sparkles, Clock, X, Check,
  Zap, Users, Cpu, Lightbulb, AlertTriangle, Send, ArrowRight, ArrowLeft, UploadCloud,
  Globe, Smartphone, Bot, TrendingUp, Mail, Wrench, Palette, ShoppingBag, Boxes, Server, PenTool,
  Loader2,
} from "lucide-react";
import toast from "react-hot-toast";
import { axiosInstance } from "../lib/axios";
import { useAuthStore } from "../store/useAuthStore";
import ClientSidebar from "../components/portal/ClientSidebar";
import ClientTopBar from "../components/portal/ClientTopBar";
import { SERVICES, CATEGORIES, serviceByKey, type CatalogService } from "../lib/servicesCatalog";

const ICONS: Record<string, any> = { Globe, Smartphone, Bot, TrendingUp, Mail, Wrench, Palette, ShoppingBag, Boxes, Server, PenTool };
const money = (n: number) => `$${Number(n || 0).toLocaleString()}`;
const badgeStyle = (b?: string) =>
  b === "Best Seller" ? "bg-amber-500" : b === "Popular" ? "bg-secondary" : b === "New" ? "bg-violet-600" : "bg-primary";

const ClientServicesPage = () => {
  const { authUser } = useAuthStore();
  const [category, setCategory] = useState("Recommended");
  const [search, setSearch] = useState("");
  const [visible, setVisible] = useState(6);
  const [detail, setDetail] = useState<CatalogService | null>(null);
  const [quoteFor, setQuoteFor] = useState<CatalogService | null>(null);
  const [requests, setRequests] = useState<any[]>([]);
  const [firstProject, setFirstProject] = useState<string>("");
  const [clientProjects, setClientProjects] = useState<any[]>([]);

  useEffect(() => {
    axiosInstance.get("/client-portal/overview").then(({ data }) => {
      setFirstProject(data?.projects?.[0]?.name || "");
      setClientProjects(data?.projects || []);
    }).catch(() => {});
    loadRequests();
  }, []);
  const loadRequests = () =>
    axiosInstance.get("/client-portal/service-requests").then(({ data }) => setRequests(data.requests || [])).catch(() => {});

  // Recommended = badge Recommended; else filter by category. Then search.
  const filtered = useMemo(() => {
    let list = category === "Recommended" ? SERVICES.filter((s) => s.badge === "Recommended") : SERVICES.filter((s) => s.category === category);
    if (category === "Recommended" && list.length < 3) list = SERVICES.slice(0, 6);
    const q = search.trim().toLowerCase();
    if (q) list = SERVICES.filter((s) =>
      [s.name, s.category, s.short, ...s.tech].join(" ").toLowerCase().includes(q));
    return list;
  }, [category, search]);

  return (
    <div className="min-h-screen bg-[#f8f9ff] text-on-surface flex">
      <ClientSidebar active="services" />

      <div className="flex-1 min-w-0">
        <ClientTopBar title="Services" />

        <main className="p-4 lg:p-8 space-y-8 max-w-[1400px] mx-auto">
          <HeroSlider onLearnMore={(s) => setDetail(s)} />

          {/* Search */}
          <div className="bg-white border border-outline-variant rounded-xl p-4 shadow-sm">
            <div className="relative">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant" />
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search services, technologies or solutions..."
                className="w-full bg-surface-container-low border border-outline-variant rounded-lg py-3 pl-12 pr-4 text-[14px] focus:ring-2 focus:ring-primary focus:border-transparent outline-none" />
            </div>
          </div>

          {/* Category tabs */}
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
            {CATEGORIES.map((c) => (
              <button key={c} onClick={() => { setCategory(c); setSearch(""); setVisible(6); }}
                className={`px-5 py-2 rounded-full whitespace-nowrap text-[13px] font-semibold transition-all ${category === c && !search ? "bg-primary text-white" : "bg-surface-container-low text-on-surface-variant hover:bg-surface-variant"}`}>
                {c}
              </button>
            ))}
          </div>

          {/* AI recommendation banner (Recommended tab) */}
          {category === "Recommended" && !search && (
            <div className="bg-primary/5 border border-primary/10 rounded-xl p-5 flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-primary/10 grid place-items-center text-primary shrink-0"><Sparkles size={18} /></div>
              <div>
                <p className="text-primary font-bold text-[11px] uppercase tracking-wider">AI Recommendation</p>
                <p className="text-on-surface-variant text-[14px] mt-0.5">
                  {firstProject
                    ? <>Based on your <span className="font-semibold text-on-surface">{firstProject}</span> project, we recommend <span className="font-semibold text-on-surface">Website Maintenance</span> and <span className="font-semibold text-on-surface">SEO Optimization</span> to maximize long-term performance and organic traffic.</>
                    : <>Based on your recent activity, we recommend an <span className="font-semibold text-on-surface">AI Chatbot</span> and <span className="font-semibold text-on-surface">SEO Audit</span> to grow engagement and organic traffic.</>}
                </p>
              </div>
            </div>
          )}

          {/* Service cards */}
          {filtered.length === 0 ? (
            <p className="text-center py-16 text-outline">No services match your search.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.slice(0, visible).map((s) => (
                <ServiceCard key={s.key} s={s} onLearnMore={() => setDetail(s)} onQuote={() => setQuoteFor(s)} />
              ))}
            </div>
          )}
          {filtered.length > visible && (
            <div className="flex justify-center">
              <button onClick={() => setVisible((v) => v + 6)} className="px-6 py-3 border border-outline-variant rounded-lg text-[13px] font-bold hover:bg-surface-container-high">Load More Services</button>
            </div>
          )}

          {/* Request History */}
          <RequestHistory requests={requests} />
        </main>
      </div>

      {detail && (
        <ServiceDetailModal service={detail} onClose={() => setDetail(null)}
          onQuote={() => { const s = detail; setDetail(null); setQuoteFor(s); }}
          onOpenService={(key) => { const s = serviceByKey(key); if (s) setDetail(s); }} />
      )}
      {quoteFor && (
        <QuoteModal service={quoteFor} projects={clientProjects} onClose={() => setQuoteFor(null)} onSubmitted={() => { setQuoteFor(null); loadRequests(); }} />
      )}
    </div>
  );
};

// ── Hero slider ──────────────────────────────────────────────────────────────
const SLIDES = [
  { key: "web-dev", badge: "Featured Transformation", title: "Enterprise Web Platform Rebuild", body: "Scale your digital presence with a fast, secure, conversion-focused platform — migrated with zero downtime.", gradient: "from-[#0a3470] to-[#001a43]" },
  { key: "ai-chatbot", badge: "AI Excellence", title: "AI Support Agent Rollout", body: "A custom AI assistant trained on your brand voice — resolving 60% of support queries instantly, 24/7.", gradient: "from-[#0e2a5e] to-[#06255a]" },
  { key: "erp", badge: "Operations Innovation", title: "Unified ERP Automation", body: "Inventory, HR and finance in one platform — streamlining operations with real-time reporting.", gradient: "from-[#123a2e] to-[#06255a]" },
];
const HeroSlider = ({ onLearnMore }: { onLearnMore: (s: CatalogService) => void }) => {
  const [i, setI] = useState(0);
  const paused = useRef(false);
  useEffect(() => {
    const t = setInterval(() => { if (!paused.current) setI((x) => (x + 1) % SLIDES.length); }, 6500);
    return () => clearInterval(t);
  }, []);
  const go = (d: number) => setI((x) => (x + d + SLIDES.length) % SLIDES.length);
  return (
    <section className="relative overflow-hidden rounded-3xl h-[360px] group"
      onMouseEnter={() => (paused.current = true)} onMouseLeave={() => (paused.current = false)}>
      {SLIDES.map((sl, idx) => (
        <div key={sl.key} className={`absolute inset-0 bg-gradient-to-r ${sl.gradient} flex items-center transition-opacity duration-500 ${idx === i ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
          <div className="relative z-10 px-8 lg:px-12 max-w-2xl space-y-4">
            <span className="inline-block px-3 py-1 bg-primary text-white text-[11px] rounded-full font-bold uppercase tracking-wider">{sl.badge}</span>
            <h2 className="text-white text-[30px] lg:text-[34px] font-bold leading-tight">{sl.title}</h2>
            <p className="text-slate-200/90 text-[15px] leading-relaxed">{sl.body}</p>
            <div className="pt-2 flex gap-3">
              <button onClick={() => { const s = serviceByKey(sl.key); if (s) onLearnMore(s); }} className="bg-primary hover:bg-primary-container text-white px-6 py-3 rounded-lg text-[13px] font-semibold flex items-center gap-2"><Zap size={16} /> Learn More</button>
              <button onClick={() => { const s = serviceByKey(sl.key); if (s) onLearnMore(s); }} className="border border-white/30 text-white hover:bg-white/10 px-6 py-3 rounded-lg text-[13px] font-semibold">View Case Study</button>
            </div>
          </div>
        </div>
      ))}
      <button onClick={() => go(-1)} className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-white/10 border border-white/20 text-white hover:bg-white/20 opacity-0 group-hover:opacity-100 transition"><ChevronLeft size={20} /></button>
      <button onClick={() => go(1)} className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-white/10 border border-white/20 text-white hover:bg-white/20 opacity-0 group-hover:opacity-100 transition"><ChevronRight size={20} /></button>
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {SLIDES.map((_, idx) => (
          <button key={idx} onClick={() => setI(idx)} className={`h-2 rounded-full transition-all ${idx === i ? "w-6 bg-white" : "w-2 bg-white/40 hover:bg-white/60"}`} />
        ))}
      </div>
    </section>
  );
};

// ── Service card ─────────────────────────────────────────────────────────────
const ServiceCard = ({ s, onLearnMore, onQuote }: { s: CatalogService; onLearnMore: () => void; onQuote: () => void }) => {
  const Icon = ICONS[s.icon] || Globe;
  return (
    <div className="bg-white border border-outline-variant rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all flex flex-col">
      <div className={`h-40 bg-gradient-to-br ${s.gradient} relative grid place-items-center`}>
        <Icon size={52} className="text-white/90" />
        {s.badge && <span className={`absolute top-4 left-4 px-3 py-1 ${badgeStyle(s.badge)} text-white rounded-full text-[11px] font-bold uppercase tracking-wider`}>{s.badge}</span>}
      </div>
      <div className="p-5 space-y-3 flex-1 flex flex-col">
        <div className="space-y-1.5">
          <div className="text-[11px] text-primary font-bold uppercase">{s.category}</div>
          <h3 className="text-[18px] font-semibold text-on-surface">{s.name}</h3>
          <div className="bg-primary/5 p-2 rounded border border-primary/10 flex items-start gap-2">
            <Sparkles size={14} className="text-primary shrink-0 mt-0.5" />
            <p className="text-[13px] text-on-surface-variant">{s.short}</p>
          </div>
        </div>
        <div className="flex justify-between items-center py-2 border-y border-outline-variant/40">
          <div className="text-[14px] font-semibold text-primary tracking-wide">{s.price}</div>
          <div className="flex items-center gap-1 text-[12px] text-on-surface-variant"><Clock size={14} /> {s.timeline}</div>
        </div>
        <div className="pt-2 mt-auto grid grid-cols-2 gap-3">
          <button onClick={onQuote} className="bg-primary text-white hover:bg-[#004b9e] py-2 rounded-lg text-[13px] font-semibold">Get a Quote</button>
          <button onClick={onLearnMore} className="text-primary border border-primary hover:bg-primary/5 py-2 rounded-lg text-[13px] font-semibold">Learn More</button>
        </div>
      </div>
    </div>
  );
};

// ── Service details modal ────────────────────────────────────────────────────
const ServiceDetailModal = ({ service, onClose, onQuote, onOpenService }: { service: CatalogService; onClose: () => void; onQuote: () => void; onOpenService: (key: string) => void }) => {
  const Icon = ICONS[service.icon] || Globe;
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className={`h-32 bg-gradient-to-br ${service.gradient} relative flex items-end p-5`}>
          <Icon size={40} className="absolute top-4 right-5 text-white/40" />
          <div>
            <div className="text-[11px] text-white/80 font-bold uppercase tracking-wider">{service.category}</div>
            <h2 className="text-[22px] font-bold text-white leading-tight">{service.name}</h2>
          </div>
          <button onClick={onClose} className="absolute top-4 left-4 p-1.5 rounded-full bg-black/20 text-white hover:bg-black/30"><X size={16} /></button>
        </div>
        <div className="p-6 overflow-y-auto space-y-6">
          <div>
            <p className="text-[14px] text-on-surface-variant leading-relaxed">{service.description}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
              <MiniBox label="Starting Price" value={service.price} />
              <MiniBox label="Typical Timeline" value={service.timeline} />
              <MiniBox label="Category" value={service.category} />
              <MiniBox label="Industries" value={service.industries.slice(0, 2).join(", ")} />
            </div>
          </div>

          <Section title="Business Benefits">
            <div className="flex flex-wrap gap-2">
              {service.benefits.map((b) => <span key={b} className="text-[12px] bg-secondary-container/50 text-[#0f5f57] rounded-full px-3 py-1">{b}</span>)}
            </div>
          </Section>

          <Section title="What's Included">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {service.deliverables.map((d) => (
                <div key={d} className="flex items-center gap-2 text-[13px] text-on-surface"><Check size={14} className="text-secondary" /> {d}</div>
              ))}
            </div>
          </Section>

          <Section title="Technology Stack">
            <div className="flex flex-wrap gap-2">
              {service.tech.map((t) => <span key={t} className="text-[12px] bg-primary/5 text-primary border border-primary/10 rounded-md px-3 py-1 font-semibold">{t}</span>)}
            </div>
          </Section>

          <Section title="Portfolio">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {service.related.slice(0, 2).map((k) => {
                const r = serviceByKey(k); if (!r) return null;
                const RIcon = ICONS[r.icon] || Globe;
                return (
                  <div key={k} className="border border-outline-variant rounded-lg overflow-hidden">
                    <div className={`h-20 bg-gradient-to-br ${r.gradient} grid place-items-center`}><RIcon size={28} className="text-white/90" /></div>
                    <div className="p-3">
                      <p className="text-[13px] font-semibold text-on-surface">{r.name} Project</p>
                      <p className="text-[11px] text-on-surface-variant">{r.industries[0]} · {r.short.slice(0, 40)}…</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>

          <Section title="Frequently Asked Questions">
            <div className="space-y-2">
              {service.faqs.map((f, i) => (
                <details key={i} className="border border-outline-variant rounded-lg p-3">
                  <summary className="text-[13px] font-semibold text-on-surface cursor-pointer">{f.q}</summary>
                  <p className="text-[13px] text-on-surface-variant mt-1.5">{f.a}</p>
                </details>
              ))}
            </div>
          </Section>

          <Section title="Related Services">
            <div className="flex flex-wrap gap-2">
              {service.related.map((k) => {
                const r = serviceByKey(k); if (!r) return null;
                return <button key={k} onClick={() => onOpenService(k)} className="text-[12px] border border-outline-variant rounded-full px-3 py-1 hover:bg-surface-container-low">{r.name}</button>;
              })}
            </div>
          </Section>
        </div>
        <div className="p-4 border-t border-outline-variant bg-surface-container-low/50 flex justify-end">
          <button onClick={onQuote} className="px-6 py-2.5 bg-primary text-white text-[13px] font-bold rounded-lg hover:bg-[#004b9e]">Get a Quote</button>
        </div>
      </div>
    </div>
  );
};
const MiniBox = ({ label, value }: { label: string; value: string }) => (
  <div className="bg-surface-container-low rounded-lg p-2.5">
    <p className="text-[10px] uppercase tracking-wider font-bold text-outline">{label}</p>
    <p className="text-[13px] font-semibold text-on-surface truncate">{value}</p>
  </div>
);
const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div>
    <h4 className="text-[11px] uppercase tracking-wider font-bold text-outline mb-2">{title}</h4>
    {children}
  </div>
);

// ── Get-a-Quote — single-step lightweight brief ──────────────────────────────
// Under-two-minutes: the client submits a short brief; the BD prepares the
// estimate and schedules a discovery meeting afterwards.
const QuoteModal = ({ service, projects, onClose, onSubmitted }: { service: CatalogService; projects: any[]; onClose: () => void; onSubmitted: () => void }) => {
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [projectTitle, setProjectTitle] = useState("");
  const [overview, setOverview] = useState("");
  const [linked, setLinked] = useState<"no" | "yes">("no");
  const [existingProject, setExistingProject] = useState("");

  const activeProjects = (projects || []).filter(
    (p: any) => !/complete|cancel/i.test(p.projectStatus || p.status || "")
  );

  const submit = async () => {
    if (!projectTitle.trim()) return toast.error("Add a project title.");
    if (!overview.trim()) return toast.error("Tell us a little about your project.");
    if (linked === "yes" && !existingProject) return toast.error("Select the linked project.");
    setBusy(true);
    try {
      await axiosInstance.post("/client-portal/service-requests", {
        service: { key: service.key, name: service.name, category: service.category },
        requestType: linked === "yes" ? "Existing Project Enhancement" : "New Project",
        existingProject: linked === "yes" ? existingProject : null,
        projectTitle,
        overview: { description: overview },
        documents: files.map((f) => ({ name: f.name, size: f.size, mimeType: f.type })),
      });
      toast.success("Quote request submitted! Your Business Developer will review it and reach out.");
      onSubmitted();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Couldn't submit your request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[75] grid place-items-center bg-black/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[94vh]">
        <div className="px-6 pt-6 pb-4 border-b border-outline-variant">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-[19px] font-bold text-on-surface">Request a Quote</h2>
              <p className="text-[13px] text-on-surface-variant mt-1">Tell us a little about your project and we'll prepare an estimate before scheduling a discovery meeting.</p>
            </div>
            <button onClick={onClose} className="p-1.5 text-outline hover:text-on-surface rounded-full hover:bg-surface-container-low"><X size={18} /></button>
          </div>
          <div className="mt-2 text-[12px] text-primary font-semibold">{service.name}</div>
        </div>

        <div className="p-6 overflow-y-auto space-y-5">
          <Field label="Project Title *">
            <input value={projectTitle} onChange={(e) => setProjectTitle(e.target.value)} placeholder="e.g., Customer Loyalty Mobile App" className={INP} />
          </Field>

          <Field label="Project Overview *">
            <textarea rows={5} value={overview} onChange={(e) => setOverview(e.target.value)}
              placeholder="Tell us about your project, goals, ideas, or requirements..." className={INP} />
            <p className="text-[11px] text-on-surface-variant mt-1">Objectives, functionality, target audience, challenges, timeline — anything helps.</p>
          </Field>

          <div>
            <label className="block text-[12px] font-semibold text-on-surface mb-2">Is this linked to an existing project?</label>
            <div className="flex gap-2">
              {([["no", "No, it's a new project"], ["yes", "Yes"]] as const).map(([val, label]) => (
                <button key={val} onClick={() => setLinked(val)}
                  className={`px-4 py-2 rounded-lg text-[13px] font-semibold border ${linked === val ? "bg-primary text-white border-primary" : "border-outline-variant text-on-surface-variant hover:bg-surface-container-low"}`}>{label}</button>
              ))}
            </div>
            {linked === "yes" && (
              <div className="mt-3">
                <select value={existingProject} onChange={(e) => setExistingProject(e.target.value)} className={INP}>
                  <option value="">Select a project…</option>
                  {activeProjects.map((p: any) => <option key={p._id} value={p._id}>{p.name}</option>)}
                </select>
                {activeProjects.length === 0 && <p className="text-[11px] text-on-surface-variant mt-1">No active projects to link.</p>}
              </div>
            )}
          </div>

          <Field label="Supporting Documents (optional)">
            <label className="border-2 border-dashed border-outline-variant rounded-lg p-5 flex flex-col items-center gap-1 cursor-pointer hover:bg-surface-container-low">
              <UploadCloud size={24} className="text-primary" />
              <span className="text-[13px] text-on-surface-variant">Drag & drop or click — PDF, Word, PPT, Excel, images, ZIP, Figma</span>
              <input type="file" multiple className="hidden" onChange={(e) => setFiles((prev) => [...prev, ...Array.from(e.target.files || [])])} />
            </label>
            {files.length > 0 && (
              <ul className="mt-2 text-[12px] text-on-surface-variant space-y-1">
                {files.map((f, i) => (
                  <li key={i} className="flex items-center justify-between"><span className="truncate">• {f.name}</span>
                    <button onClick={() => setFiles((p) => p.filter((_, j) => j !== i))} className="text-outline hover:text-error"><X size={13} /></button>
                  </li>
                ))}
              </ul>
            )}
          </Field>
        </div>

        <div className="p-4 border-t border-outline-variant bg-surface-container-low/50 flex justify-end gap-2">
          <button onClick={onClose} className="px-5 py-2.5 text-[13px] font-bold text-on-surface-variant hover:bg-surface-container rounded-lg">Cancel</button>
          <button onClick={submit} disabled={busy} className="px-6 py-2.5 bg-primary text-white text-[13px] font-bold rounded-lg hover:bg-[#004b9e] inline-flex items-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Submit Quote Request
          </button>
        </div>
      </div>
    </div>
  );
};

const INP = "w-full bg-white border border-outline-variant rounded-lg p-2.5 text-[13px] focus:ring-2 focus:ring-primary focus:border-transparent outline-none";
const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div><label className="block text-[12px] font-semibold text-on-surface mb-1.5">{label}</label>{children}</div>
);

// ── Request history ──────────────────────────────────────────────────────────
const statusChip = (s: string) => {
  if (/converted|approved|closed/i.test(s)) return "bg-secondary-container text-[#0f5f57]";
  if (/submitted|review|scheduled|progress|sent|awaiting/i.test(s)) return "bg-primary/10 text-primary";
  if (/draft/i.test(s)) return "bg-surface-container-high text-on-surface-variant";
  return "bg-surface-container-high text-on-surface-variant";
};
const RequestHistory = ({ requests }: { requests: any[] }) => (
  <section className="bg-white border border-outline-variant rounded-xl overflow-hidden shadow-sm">
    <div className="px-5 py-4 border-b border-outline-variant"><h3 className="text-[16px] font-bold text-on-surface">Request History</h3></div>
    <div className="overflow-x-auto">
      <table className="w-full text-left text-[13px]">
        <thead>
          <tr className="text-[11px] uppercase tracking-wider text-on-surface-variant border-b border-outline-variant">
            <th className="px-5 py-3 font-semibold">Request</th><th className="px-5 py-3 font-semibold">Service</th><th className="px-5 py-3 font-semibold">Submitted</th><th className="px-5 py-3 font-semibold">Status</th><th className="px-5 py-3 font-semibold">Representative</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-outline-variant">
          {requests.length === 0 ? (
            <tr><td colSpan={5} className="px-5 py-10 text-center text-outline">No service requests yet. Explore services above and click “Get a Quote”.</td></tr>
          ) : requests.map((r) => (
            <tr key={r._id} className="hover:bg-surface-container-low/50">
              <td className="px-5 py-3.5 font-semibold text-on-surface">{r.projectTitle}</td>
              <td className="px-5 py-3.5 text-on-surface-variant">{r.service?.name}</td>
              <td className="px-5 py-3.5 text-on-surface-variant">{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : "—"}</td>
              <td className="px-5 py-3.5"><span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${statusChip(r.status)}`}>{r.status}</span></td>
              <td className="px-5 py-3.5 text-on-surface-variant">{r.assignedTo?.fullName || "Pending assignment"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </section>
);

export default ClientServicesPage;
